/**
 * Task display helpers (Story 28.2) — shared by TaskTableView, TaskBoard,
 * TaskFilterToolbar, and TaskFormDialog so status / priority / category /
 * assignee / due render identically everywhere.
 */

import {
  Circle,
  CircleHalf,
  CheckCircle,
  type Icon,
} from "@phosphor-icons/react";
import type { TaskStatus, TaskPriority, TaskRecurrenceRule } from "./types";

/** Status dropdown options (the inline status control + filters). */
export const STATUS_OPTIONS: { value: TaskStatus; label: string }[] = [
  { value: "todo", label: "To Do" },
  { value: "in_progress", label: "In Progress" },
  { value: "done", label: "Done" },
];

export function statusLabel(status: TaskStatus): string {
  return STATUS_OPTIONS.find((o) => o.value === status)?.label ?? status;
}

/**
 * Status pill colors: TO DO violet · IN PROGRESS blue · DONE green — three
 * distinct, vibrant tints (bg-100/text-700, matching the reports status pills).
 */
export const STATUS_STYLES: Record<TaskStatus, string> = {
  todo: "bg-violet-100 text-violet-700",
  in_progress: "bg-blue-100 text-blue-700",
  done: "bg-green-100 text-green-700",
};

/**
 * Phosphor status icons (hollow circle → half-filled → check), rendered inside
 * the status pill — the icon inherits the pill's text tint via currentColor.
 */
export const STATUS_ICONS: Record<TaskStatus, Icon> = {
  todo: Circle,
  in_progress: CircleHalf,
  done: CheckCircle,
};

/** Priority dropdown options. */
export const PRIORITY_OPTIONS: { value: TaskPriority; label: string }[] = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
];

export function priorityLabel(priority: TaskPriority): string {
  return PRIORITY_OPTIONS.find((o) => o.value === priority)?.label ?? priority;
}

export const PRIORITY_STYLES: Record<TaskPriority, string> = {
  low: "bg-muted text-muted-foreground",
  medium: "bg-amber-100 text-amber-700",
  high: "bg-red-100 text-red-700",
};

/** Recurrence dropdown options (the create dialog's rule picker). */
export const RECURRENCE_OPTIONS: { value: TaskRecurrenceRule; label: string }[] =
  [
    { value: "daily", label: "Daily" },
    { value: "weekly", label: "Weekly" },
    { value: "biweekly", label: "Bi-weekly" },
    { value: "monthly", label: "Monthly" },
  ];

export function recurrenceLabel(rule: TaskRecurrenceRule): string {
  return RECURRENCE_OPTIONS.find((o) => o.value === rule)?.label ?? rule;
}

/** "—" or a short locale date for the due-date column. */
export function dueLabel(ms: number | null): string {
  if (ms === null) return "—";
  return new Date(ms).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** Overdue = a due date in the past on a not-yet-done task (drives red styling). */
export function isOverdue(dueMs: number | null, status: TaskStatus): boolean {
  if (dueMs === null || status === "done") return false;
  return dueMs < Date.now();
}

/**
 * NOTE: the category helpers below are UNUSED by the UI since tasks moved to
 * tags — kept only because the `category` DB column is intentionally
 * dormant/reversible (no UI wires them).
 */
export function categoryLabel(category: string | null): string {
  return category ?? "—";
}

export function assigneeLabel(name: string | null): string {
  return name ?? "Unassigned";
}

/** Distinct non-empty categories (retained for the dormant category column). */
export function distinctCategories(
  tasks: readonly { category: string | null }[],
): string[] {
  const set = new Set<string>();
  for (const t of tasks) {
    if (t.category && t.category.trim()) set.add(t.category);
  }
  return [...set].sort((a, b) => a.localeCompare(b));
}

/** Distinct assignees (id + display name) across loaded tasks (assignee filter). */
export function distinctAssignees(
  tasks: readonly { assignedTo: string | null; assigneeName: string | null }[],
): { id: string; name: string }[] {
  const map = new Map<string, string>();
  for (const t of tasks) {
    if (t.assignedTo) map.set(t.assignedTo, t.assigneeName ?? t.assignedTo);
  }
  return [...map.entries()]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}
